import { formatApiErrorDetail } from "@/lib/api";
// RenterPortal — the magic-link tenant view at /rent/:token
// No login. No password. Mobile-first. Three actions:
//   1. See rent status (this month + last 6)
//   2. Pay rent via Stripe Checkout (ACH + card)
//   3. Report a repair (goes to landlord's inbox for AI triage)
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  House, CreditCard, Wrench, CheckCircle, Sparkle, ArrowRight, ShieldCheck,
  ClockCounterClockwise, Warning,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function RenterPortal() {
  const { token } = useParams();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [repairForm, setRepairForm] = useState({ title: "", description: "", submitter_name: "", submitter_phone: "" });
  const [showRepair, setShowRepair] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/rent-portal/${token}`);
      setData(r.data);
      if (r.data?.rental?.renter_name && !repairForm.submitter_name) {
        setRepairForm(f => ({ ...f, submitter_name: r.data.rental.renter_name }));
      }
    } catch (e) {
      setErr(e?.response?.status === 404 ? "This portal link isn't active. Ask your landlord for a fresh one." : "Something went wrong loading your portal.");
    }
  };
  useEffect(() => { load(); }, [token]);

  // Handle Stripe redirect ?paid=1&session_id=...
  useEffect(() => {
    const paid = sp.get("paid"); const sess = sp.get("session_id");
    if (paid === "1" && sess) {
      (async () => {
        try {
          const r = await axios.post(`${API}/public/rent-portal/${token}/pay-confirm?session_id=${encodeURIComponent(sess)}`);
          if (r.data?.ok && !r.data.already_recorded) {
            toast.success(`Rent paid — ${dollars(r.data.amount_cents)} · thank you!`);
          } else if (r.data?.already_recorded) {
            toast.info("Payment already recorded.");
          }
        } catch { toast.error("Could not confirm payment — refresh in a minute."); }
        // Clean the URL so a refresh doesn't re-confirm
        sp.delete("paid"); sp.delete("session_id"); setSp(sp);
        load();
      })();
    } else if (paid === "0") {
      toast.info("Payment cancelled.");
      sp.delete("paid"); setSp(sp);
    }
  }, []);  // eslint-disable-line

  const pay = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/public/rent-portal/${token}/pay-checkout`);
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else { toast.error("Could not start checkout"); }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Checkout failed")); }
    finally { setPaying(false); }
  };

  const submitRepair = async (e) => {
    e?.preventDefault?.();
    if (!repairForm.title || repairForm.title.length < 2) { toast.error("Give it a short title"); return; }
    if (!repairForm.description || repairForm.description.length < 5) { toast.error("Add more detail"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/rent-portal/${token}/repair`, repairForm);
      toast.success(r.data.message || "Sent to your landlord");
      setShowRepair(false);
      setRepairForm(f => ({ ...f, title: "", description: "" }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Submit failed")); }
    finally { setSubmitting(false); }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6" data-testid="renter-portal-error">
        <div className="max-w-md text-center">
          <Warning weight="fill" size={48} className="text-amber-400 mx-auto mb-4"/>
          <div className="font-display font-black text-2xl mb-2">Portal link inactive</div>
          <div className="text-sm text-slate-400">{err}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-400">Loading your portal…</div>;
  }

  const r = data.rental;
  const monthLabel = new Date().toLocaleString("en-US", { month: "long", year: "numeric" });
  const paidThisMonth = (data.payments || []).some(p => {
    const now = new Date();
    return p.period === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="renter-portal">
      <Toaster position="top-center" richColors/>
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-lg mx-auto px-5 py-8">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-9 w-9 rounded-lg bg-sky-500 flex items-center justify-center"><House weight="fill" size={20}/></div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Renter Portal</div>
          </div>
          <div className="font-display font-black text-3xl leading-tight" data-testid="renter-portal-unit">{r.unit_label}</div>
          <div className="text-sm text-slate-400 mt-1">{r.address}</div>
          <div className="text-xs text-slate-500 mt-3">Managed by <span className="font-bold text-slate-200">{data.landlord_name}</span></div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-5 -mt-6 pb-16 space-y-4">
        {/* Note from landlord */}
        {r.notes_to_renter && (
          <div className="bg-white border-2 border-sky-200 rounded-2xl p-4 shadow-sm" data-testid="renter-portal-note">
            <div className="text-[10px] uppercase font-black tracking-widest text-sky-700 mb-1">Note from your landlord</div>
            <div className="text-sm text-slate-800">{r.notes_to_renter}</div>
          </div>
        )}

        {/* Rent card */}
        <div className="bg-white rounded-2xl p-5 shadow-md" data-testid="renter-portal-rent-card">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard weight="fill" size={18} className="text-emerald-600"/>
            <div className="text-[10px] uppercase font-black tracking-widest text-slate-500">Rent</div>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="font-display font-black text-4xl text-slate-900" data-testid="renter-portal-rent-amount">{dollars(r.rent_monthly_cents)}</div>
            <div className="text-xs text-slate-500 font-bold">/ month</div>
          </div>
          <div className="text-xs text-slate-500 mt-1">Due day {r.rent_due_day} · {monthLabel}</div>
          {paidThisMonth ? (
            <div className="mt-4 p-3 rounded-lg bg-emerald-50 border-l-4 border-emerald-500 text-emerald-900 text-sm inline-flex items-center gap-2" data-testid="renter-portal-paid-badge">
              <CheckCircle weight="fill" size={16}/> Paid for {monthLabel}
            </div>
          ) : (
            <button onClick={pay} disabled={paying} data-testid="renter-portal-pay-btn"
              className="mt-4 w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-sm rounded-xl inline-flex items-center justify-center gap-2 shadow-lg disabled:opacity-60">
              <CreditCard weight="fill" size={18}/> {paying ? "Opening checkout…" : `Pay ${dollars(r.rent_monthly_cents)} Now`}
              <ArrowRight size={16}/>
            </button>
          )}
          <div className="mt-3 text-[10px] text-slate-400 text-center inline-flex items-center gap-1 w-full justify-center">
            <ShieldCheck size={11}/> Secured by Stripe · ACH or card
          </div>
        </div>

        {/* Report a repair */}
        <div className="bg-white rounded-2xl p-5 shadow-md" data-testid="renter-portal-repair-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench weight="fill" size={18} className="text-sky-600"/>
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500">Repairs</div>
            </div>
            {!showRepair && (
              <button onClick={() => setShowRepair(true)} data-testid="renter-portal-repair-open-btn"
                className="h-9 px-3 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-[10px] rounded-lg inline-flex items-center gap-1 shadow">
                <Sparkle weight="fill" size={12}/> Report Repair
              </button>
            )}
          </div>

          {showRepair && (
            <form onSubmit={submitRepair} className="mt-4 space-y-2" data-testid="renter-portal-repair-form">
              <input value={repairForm.title} onChange={e => setRepairForm(f => ({ ...f, title: e.target.value }))}
                placeholder="What's broken? (short)"
                data-testid="renter-portal-repair-title"
                className="w-full h-11 px-3 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/>
              <textarea rows={4} value={repairForm.description} onChange={e => setRepairForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Tell us more — what happened, when, and where in the unit"
                data-testid="renter-portal-repair-desc"
                className="w-full px-3 py-2 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/>
              <input value={repairForm.submitter_name} onChange={e => setRepairForm(f => ({ ...f, submitter_name: e.target.value }))}
                placeholder="Your name"
                data-testid="renter-portal-repair-name"
                className="w-full h-11 px-3 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/>
              <input value={repairForm.submitter_phone} onChange={e => setRepairForm(f => ({ ...f, submitter_phone: e.target.value }))}
                placeholder="Your callback phone (optional)"
                data-testid="renter-portal-repair-phone"
                className="w-full h-11 px-3 border-2 border-slate-200 focus:border-sky-500 outline-none rounded-lg font-medium"/>
              <div className="flex items-center gap-2 pt-1">
                <button type="button" onClick={() => setShowRepair(false)} className="h-11 px-4 border-2 border-slate-200 text-slate-600 font-black uppercase tracking-widest text-xs rounded-lg">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="renter-portal-repair-submit"
                  className="flex-1 h-11 bg-sky-600 hover:bg-sky-700 text-white font-black uppercase tracking-widest text-xs rounded-lg inline-flex items-center justify-center gap-2 shadow disabled:opacity-60">
                  <Sparkle weight="fill" size={14}/> {submitting ? "Sending…" : "Send to Landlord"}
                </button>
              </div>
              <div className="text-[10px] text-slate-400 text-center pt-1">
                For a gas smell, fire, flood, or medical emergency — call 911 first.
              </div>
            </form>
          )}

          {(data.repairs || []).length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500">Your recent requests</div>
              {data.repairs.map(rep => (
                <div key={rep.id} data-testid={`renter-portal-repair-${rep.id}`}
                  className="p-3 border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm">{rep.title}</span>
                    <span className={`text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${
                      rep.status === "dispatched" || rep.status === "in_progress" ? "bg-emerald-100 text-emerald-700" :
                      rep.status === "resolved" ? "bg-slate-100 text-slate-500" :
                      rep.status === "triaged" ? "bg-sky-100 text-sky-700" :
                      "bg-fuchsia-100 text-fuchsia-700"
                    }`}>{rep.status.replace("_", " ")}</span>
                  </div>
                  {rep.renter_advice && (
                    <div className="text-xs text-sky-800 bg-sky-50 rounded p-2 mt-1.5 border-l-4 border-sky-500">
                      <b>Landlord's note:</b> {rep.renter_advice}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400 mt-1">{rep.created_at?.slice(0, 10)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment history */}
        {(data.payments || []).length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm" data-testid="renter-portal-history">
            <div className="flex items-center gap-2 mb-2">
              <ClockCounterClockwise weight="fill" size={18} className="text-slate-500"/>
              <div className="text-[10px] uppercase font-black tracking-widest text-slate-500">Payment history</div>
            </div>
            <div className="divide-y divide-slate-100">
              {data.payments.map((p, i) => (
                <div key={i} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-bold">{p.period}</div>
                    <div className="text-[10px] text-slate-400 uppercase font-black tracking-widest">{p.method}</div>
                  </div>
                  <div className="font-mono font-bold text-emerald-700">{dollars(p.amount_cents)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-[10px] text-slate-400 pt-2">
          Powered by Field Radar · Landlord Autopilot
        </div>
      </div>
    </div>
  );
}
